import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  MoreVert,
  NoteAdd,
  PersonOutline,
  EventBusy,
  VolumeOff,
  FactCheck,
  Download,
  Stop,
  OpenInNew,
} from "@mui/icons-material";

/**
 * Per-finding action surface.
 *
 * Replaces the old DastTriageActions stacked form: one trigger (a Button in
 * the drawer header, an IconButton in table rows) opens a menu; each menu
 * item opens a tiny focused modal that does one thing. Nothing is stuffed
 * into a long drawer scroll — operators pick the action, see only what
 * matters for that action, save, done.
 *
 * All writes go through the CP's enriched DAST surface:
 *   PATCH /dast/findings/:finding_id       (notes, owner, state, exception)
 *   POST  /dast/suppression-rules          (class-level suppression)
 *   POST  /dast/scans/:scan_id/stop        (cancel in-flight scan)
 *   GET   /dast/scans/:scan_id/report      (HTML report, served inline)
 *
 * Pass `variant="row"` for the compact IconButton used in each table row,
 * or omit for the drawer-header Button with "Actions ▾".
 */

const ACTIONS = [
  { id: "notes",       label: "Add or edit note",   icon: NoteAdd,       group: "triage" },
  { id: "owner",       label: "Assign owner",       icon: PersonOutline, group: "triage" },
  { id: "state",       label: "Change analyst state", icon: FactCheck,   group: "triage" },
  { id: "exception",   label: "Request exception",  icon: EventBusy,     group: "triage" },
  { id: "suppression", label: "Suppress whole class", icon: VolumeOff,   group: "triage" },
  { id: "report",      label: "Download HTML report", icon: Download,    group: "scan" },
  { id: "cancel",      label: "Cancel scan",         icon: Stop,         group: "scan", danger: true },
];

export default function DastActions({
  finding,
  variant,
  colors,
  onSaved,
  updateFinding,
  stopScan,
  getScanReportUrl,
  createSuppressionRule,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openAction, setOpenAction] = useState(null); // "notes" | "owner" | ...
  const [snack, setSnack] = useState(null);

  const findingId = finding?.finding_id || finding?.findingId;
  const scanId = finding?.latest_scan_id || finding?.latestScanId;
  const canTriage = Boolean(findingId);
  const canScanAction = Boolean(scanId);

  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const pickAction = (id) => {
    setMenuAnchor(null);
    if (id === "report") {
      // Report is a plain link — new tab, no modal.
      const href = getScanReportUrl?.(scanId);
      if (href) window.open(href, "_blank", "noopener");
      return;
    }
    setOpenAction(id);
  };

  const closeAction = () => setOpenAction(null);

  const flash = (msg, kind = "success") => setSnack({ msg, kind });

  const handleSaved = (msg) => {
    flash(msg);
    closeAction();
    onSaved?.();
  };

  const items = ACTIONS.filter((a) => {
    if (a.group === "triage") return canTriage;
    if (a.group === "scan") return canScanAction;
    return true;
  });

  const trigger = variant === "row" ? (
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); openMenu(e); }}
      aria-label="Finding actions"
      sx={{ color: colors.neutral[600] }}
    >
      <MoreVert fontSize="small" />
    </IconButton>
  ) : (
    <Button
      variant="outlined"
      onClick={openMenu}
      endIcon={<MoreVert />}
      sx={{
        textTransform: "none",
        fontSize: 14,
        color: colors.primary[300],
        borderColor: colors.primary[300],
        borderRadius: "8px",
        px: 3,
        py: 1,
      }}
    >
      Actions
    </Button>
  );

  return (
    <>
      {trigger}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { minWidth: 240, borderRadius: "8px" } }}
      >
        {items.map((a, idx) => {
          const Icon = a.icon;
          const isFirstScanAction =
            a.group === "scan" && idx > 0 && items[idx - 1]?.group !== "scan";
          return [
            isFirstScanAction && <Divider key={`div-${a.id}`} sx={{ my: 0.5 }} />,
            <MenuItem
              key={a.id}
              onClick={() => pickAction(a.id)}
              sx={{ fontSize: 13, color: a.danger ? colors.red?.[300] || "#B91C1C" : colors.neutral[1000] }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 13 }}>
                {a.label}
                {a.id === "report" && <OpenInNew sx={{ fontSize: 11, ml: 0.5, verticalAlign: "middle" }} />}
              </ListItemText>
            </MenuItem>,
          ];
        })}
      </Menu>

      {openAction === "notes" && (
        <NotesModal
          finding={finding}
          colors={colors}
          onClose={closeAction}
          onSave={async (notes) => {
            const r = await updateFinding(findingId, { analystNotes: notes || null });
            if (r) handleSaved("Note saved");
            else flash("Save failed", "error");
          }}
        />
      )}

      {openAction === "owner" && (
        <OwnerModal
          finding={finding}
          colors={colors}
          onClose={closeAction}
          onSave={async (owner) => {
            const r = await updateFinding(findingId, { owner: owner || null });
            if (r) handleSaved(owner ? `Assigned to ${owner}` : "Owner cleared");
            else flash("Save failed", "error");
          }}
        />
      )}

      {openAction === "state" && (
        <StateModal
          finding={finding}
          colors={colors}
          onClose={closeAction}
          onSave={async (state) => {
            const r = await updateFinding(findingId, { analystState: state });
            if (r) handleSaved(`State → ${state.replace("_", " ")}`);
            else flash("Save failed", "error");
          }}
        />
      )}

      {openAction === "exception" && (
        <ExceptionModal
          finding={finding}
          colors={colors}
          onClose={closeAction}
          onRequest={async ({ reason, expiresAt }) => {
            const r = await updateFinding(findingId, {
              exceptionStatus: "requested",
              exceptionReason: reason || null,
              exceptionExpiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            });
            if (r) handleSaved("Exception requested");
            else flash("Request failed", "error");
          }}
          onClear={async () => {
            const r = await updateFinding(findingId, {
              exceptionStatus: "none",
              exceptionReason: null,
              exceptionExpiresAt: null,
            });
            if (r) handleSaved("Exception cleared");
            else flash("Clear failed", "error");
          }}
        />
      )}

      {openAction === "suppression" && (
        <SuppressionModal
          finding={finding}
          colors={colors}
          onClose={closeAction}
          onCreate={async (reason) => {
            const r = await createSuppressionRule({
              pluginId: String(finding.plugin_id || finding.pluginId),
              targetId: finding.target_id || finding.targetId || null,
              reason: reason || "muted-by-analyst",
            });
            if (r) handleSaved("Suppression rule created");
            else flash("Suppression failed", "error");
          }}
        />
      )}

      {openAction === "cancel" && (
        <CancelScanModal
          scanId={scanId}
          colors={colors}
          onClose={closeAction}
          onConfirm={async () => {
            const r = await stopScan(scanId);
            if (r) handleSaved("Scan stop requested");
            else flash("Stop failed", "error");
          }}
        />
      )}

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snack && <Alert severity={snack.kind} onClose={() => setSnack(null)} variant="filled">{snack.msg}</Alert>}
      </Snackbar>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Individual modals — one small focused form each.
// ─────────────────────────────────────────────────────────────────────────────

function ModalShell({ title, subtitle, onClose, children, actions }) {
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>{subtitle}</Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>
    </Dialog>
  );
}

function NotesModal({ finding, onClose, onSave }) {
  const existing = finding?.analyst_notes || finding?.analystNotes || "";
  const [value, setValue] = useState(existing);
  const [saving, setSaving] = useState(false);
  const unchanged = value === existing;
  return (
    <ModalShell
      title={existing ? "Edit analyst note" : "Add analyst note"}
      subtitle={finding?.title}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || unchanged}
            onClick={async () => { setSaving(true); await onSave(value); setSaving(false); }}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Save note"}
          </Button>
        </>
      }
    >
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={5}
        maxRows={14}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="What did you observe? Who did you talk to? What are the next steps?"
        inputProps={{ maxLength: 4000 }}
        helperText={`${value.length} / 4000`}
      />
    </ModalShell>
  );
}

function OwnerModal({ finding, onClose, onSave }) {
  const existing = finding?.owner || "";
  const [value, setValue] = useState(existing);
  const [saving, setSaving] = useState(false);
  const unchanged = value === existing;
  return (
    <ModalShell
      title="Assign owner"
      subtitle={finding?.title}
      onClose={onClose}
      actions={
        <>
          {existing && (
            <Button
              color="warning"
              onClick={async () => { setSaving(true); await onSave(""); setSaving(false); }}
              disabled={saving}
              sx={{ textTransform: "none", mr: "auto" }}
            >
              Unassign
            </Button>
          )}
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || unchanged || !value}
            onClick={async () => { setSaving(true); await onSave(value); setSaving(false); }}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Assign"}
          </Button>
        </>
      }
    >
      <TextField
        autoFocus
        fullWidth
        label="Owner email"
        placeholder="person@layerd.ai"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        inputProps={{ maxLength: 255 }}
      />
    </ModalShell>
  );
}

function StateModal({ finding, onClose, onSave }) {
  const current = finding?.analyst_state || finding?.analystState || "open";
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const STATES = [
    { id: "open",            label: "Open — unreviewed" },
    { id: "false_positive",  label: "False positive — not a real issue" },
    { id: "accepted",        label: "Accepted — risk acknowledged, no fix planned" },
    { id: "fixed",           label: "Fixed — remediated in production" },
  ];
  return (
    <ModalShell
      title="Change analyst state"
      subtitle={finding?.title}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || value === current}
            onClick={async () => { setSaving(true); await onSave(value); setSaving(false); }}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Apply"}
          </Button>
        </>
      }
    >
      <FormControl fullWidth>
        <InputLabel>Analyst state</InputLabel>
        <Select
          autoFocus
          value={value}
          label="Analyst state"
          onChange={(e) => setValue(e.target.value)}
        >
          {STATES.map((s) => (
            <MenuItem key={s.id} value={s.id} selected={s.id === value}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ModalShell>
  );
}

function ExceptionModal({ finding, onClose, onRequest, onClear }) {
  const active = (finding?.exception_status || finding?.exceptionStatus || "none") !== "none";
  const [reason, setReason] = useState(finding?.exception_reason || finding?.exceptionReason || "");
  const [expiresAt, setExpiresAt] = useState(
    finding?.exception_expires_at || finding?.exceptionExpiresAt
      ? new Date(finding.exception_expires_at || finding.exceptionExpiresAt).toISOString().slice(0, 10)
      : ""
  );
  const [saving, setSaving] = useState(false);
  return (
    <ModalShell
      title={active ? "Update exception" : "Request exception"}
      subtitle={active ? `Currently ${finding?.exception_status || finding?.exceptionStatus}` : finding?.title}
      onClose={onClose}
      actions={
        <>
          {active && (
            <Button
              color="warning"
              onClick={async () => { setSaving(true); await onClear(); setSaving(false); }}
              disabled={saving}
              sx={{ textTransform: "none", mr: "auto" }}
            >
              Clear exception
            </Button>
          )}
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => { setSaving(true); await onRequest({ reason, expiresAt }); setSaving(false); }}
            disabled={saving || !reason}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Requesting…" : (active ? "Update" : "Request")}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <TextField
          autoFocus
          fullWidth
          label="Reason"
          placeholder="e.g. accepted by legal pending next review"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          multiline
          minRows={2}
          maxRows={6}
        />
        <TextField
          fullWidth
          type="date"
          label="Expires (optional — leaves it permanent if blank)"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </ModalShell>
  );
}

function SuppressionModal({ finding, onClose, onCreate }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <ModalShell
      title="Suppress whole class"
      subtitle={`Mutes every current + future finding with plugin ${finding?.plugin_id || finding?.pluginId || "?"} on this target.`}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => { setSaving(true); await onCreate(reason); setSaving(false); }}
            disabled={saving || !(finding?.plugin_id || finding?.pluginId)}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Creating…" : "Create suppression rule"}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          Rule applies to <Chip size="small" label={`plugin ${finding?.plugin_id || finding?.pluginId || "?"}`} sx={{ fontFamily: "monospace", fontSize: 11, ml: 0.5 }} /> scoped to this target. Revoke it from the Suppression Rules page.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Reason"
          placeholder="e.g. acceptable-by-policy / tracked-in-jira-123"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 2000 }}
        />
      </Stack>
    </ModalShell>
  );
}

function CancelScanModal({ scanId, onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);
  return (
    <ModalShell
      title="Cancel in-flight scan?"
      subtitle={`Scan ${String(scanId).slice(0, 8)}…`}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Keep running</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => { setSaving(true); await onConfirm(); setSaving(false); }}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? "…" : "Cancel scan"}
          </Button>
        </>
      }
    >
      <Typography sx={{ fontSize: 14 }}>
        Partial findings observed so far will be kept. The scan status becomes <Chip size="small" label="cancelled" sx={{ ml: 0.5 }} /> and no further work will happen on it.
      </Typography>
    </ModalShell>
  );
}
